import { useState } from "react";
import { useGruposDespesas, GrupoDespesas, GrupoDespesasFormData } from "@/features/contabil/hooks/useGruposDespesas";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Ban } from "lucide-react";
import { usePaginacaoLocal } from "@/hooks/usePaginacaoLocal";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";

export default function GruposDespesaReceita() {
  const { grupos, isLoading, createGrupo, updateGrupo, deleteGrupo } = useGruposDespesas();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<GrupoDespesas | null>(null);
  const [inativarId, setInativarId] = useState<number | null>(null);
  const [formData, setFormData] = useState<GrupoDespesasFormData>({
    descricao: "", referencia: "", isInativo: false,
  });
  const [busca, setBusca] = useState("");

  const handleOpen = (item?: GrupoDespesas) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        descricao: item.descricao || "", referencia: item.referencia || "",
        isInativo: item.isInativo,
      });
    } else {
      setEditingItem(null);
      setFormData({ descricao: "", referencia: "", isInativo: false });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingItem) {
      await updateGrupo.mutateAsync({ id: editingItem.id, ...formData });
    } else {
      await createGrupo.mutateAsync(formData);
    }
    setDialogOpen(false);
  };

  const gruposFiltrados = busca
    ? grupos.filter(g =>
        g.descricao?.toLowerCase().includes(busca.toLowerCase()) ||
        (g.referencia ?? "").toLowerCase().includes(busca.toLowerCase()))
    : grupos;

  const paginacao = usePaginacaoLocal(gruposFiltrados);

  if (isLoading) return <div className="p-6">Carregando...</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Grupos de Despesa/Receita</h1>
          <p className="text-muted-foreground">Gerencie os grupos de despesa e receita</p>
        </div>
        <Button onClick={() => handleOpen()}>
          <Plus className="h-4 w-4 mr-2" />Novo Grupo
        </Button>
      </div>

      {/* Busca */}
      <div className="max-w-sm">
        <Input
          placeholder="Buscar por descrição ou referência..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
        />
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Descrição</TableHead>
              <TableHead>Referência</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="w-[100px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginacao.itensPaginados.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="font-medium">{item.descricao || "-"}</TableCell>
                <TableCell>{item.referencia || "-"}</TableCell>
                <TableCell className="text-center">
                  <Badge variant={!item.isInativo ? "success" : "secondary"}>{!item.isInativo ? "Ativo" : "Inativo"}</Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleOpen(item)}><Pencil className="h-4 w-4" /></Button>
                    {!item.isInativo && (
                      <Button variant="ghost" size="icon" onClick={() => setInativarId(item.id)}><Ban className="h-4 w-4" /></Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {paginacao.itensPaginados.length === 0 && (
              <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">{busca ? "Nenhum grupo encontrado para esta busca." : "Nenhum grupo cadastrado"}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <PaginacaoRodape
        pagina={paginacao.pagina}
        totalPaginas={paginacao.totalPaginas}
        totalItens={paginacao.totalItens}
        primeiroItem={paginacao.primeiroItem}
        ultimoItem={paginacao.ultimoItem}
        tamanhoPagina={paginacao.tamanhoPagina}
        onPaginaChange={paginacao.setPagina}
        onTamanhoPaginaChange={paginacao.setTamanhoPagina}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>{editingItem ? "Editar Grupo" : "Novo Grupo"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Descrição *</Label>
              <Input value={formData.descricao} onChange={(e) => setFormData({ ...formData, descricao: e.target.value })} required />
            </div>
            <div className="space-y-2">
              <Label>Referência</Label>
              <Input value={formData.referencia} onChange={(e) => setFormData({ ...formData, referencia: e.target.value })} />
            </div>
            <div className="flex items-center gap-2">
              <Switch id="ativo" checked={!formData.isInativo} onCheckedChange={(v) => setFormData({ ...formData, isInativo: !v })} />
              <Label htmlFor="ativo">Ativo</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit">Salvar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!inativarId} onOpenChange={() => setInativarId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Inativar grupo</AlertDialogTitle>
            <AlertDialogDescription>Tem certeza que deseja inativar este grupo?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (inativarId) { deleteGrupo.mutate(inativarId); setInativarId(null); } }}>Inativar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
